import { rotuloDoEscopo } from '../utils/specialContractScope'
import type { ModalidadeDoContrato, EscopoDoContrato } from '../utils/specialContractScope'
import type { AcumuloDoVoucher, LiberacaoDoVoucher, MemoriaDoVoucher } from '../utils/voucher'
import { apiClient } from './api'

/** Modalidades de contrato especial (#1107 acrescentou o voucher, que não tem escopo). */
export type TipoDeContratoEspecial = 'resource_block' | 'simple_discount' | 'voucher'

// ── Types ────────────────────────────────────────────────────────────────

export type SpecialContract = {
  id: string
  name: string
  contract_type: TipoDeContratoEspecial
  status: 'active' | 'expired' | 'cancelled'
  reseller_id: string
  customer_id: string
  created_by: string
  start_date: string
  end_date: string | null
  currency: string
  notes: string | null

  // Resource Block
  block_vcpu_limit: number | null
  block_memory_gb_limit: number | null
  block_storage_gb_limit: number | null
  block_fixed_price: number | null
  block_billing_cycle: string | null
  overage_price_per_vcpu: number | null
  overage_price_per_memory_gb: number | null
  overage_price_per_storage_gb: number | null
  block_scope: string | null
  block_target_product_type: string | null
  block_target_product_id: string | null

  // Simple Discount
  discount_percentage: number | null
  discount_scope: string | null
  discount_target_product_type: string | null
  discount_target_product_id: string | null

  // Voucher (#1107) — dinheiro como string com 2 casas
  voucher_credit_amount?: string | null
  voucher_price?: string | null
  voucher_discount_amount?: string | null
  voucher_rollover?: AcumuloDoVoucher | null
  voucher_release_policy?: LiberacaoDoVoucher | null
  voucher_grace_days?: number | null
  voucher_late_payment_carry?: boolean
  /**
   * só na LISTA, no voucher: o crédito e o valor negociado que valem AGORA. As colunas
   * `voucher_*` já espelham a revisão agendada (vale na próxima competência).
   */
  voucher_vigente?: { credito_nominal: string | null; valor_negociado: string | null } | null
  /** só no detalhe de um voucher */
  voucher?: ResumoDoVoucher

  created_at: string
  updated_at: string

  // Enriched
  customer_name?: string
  reseller_name?: string
  current_vcpu_usage?: number
  current_memory_gb_usage?: number
  current_storage_gb_usage?: number
  overage_vcpu?: number
  overage_memory_gb?: number
  overage_storage_gb?: number
  vm_count?: number
  target_product_name?: string | null
}

/** Cobrança P de uma competência do voucher (a da competência financiada não tem `cobranca_id`). */
export type CobrancaDoVoucher = {
  competencia: string
  valor: string
  status: string | null
  vencimento: string
  pago: boolean
  financiada_por: string | null
  cobranca_id?: string | null
  credito_liberado: boolean
}

export type LancamentoDoVoucher = {
  id?: string
  reference_type: string
  type?: string
  amount: string
  competence_period: string | null
  description?: string | null
  created_at: string
  calculo: MemoriaDoVoucher | null
}

/** Concessão, consumo, saldo, virada e cobranças do voucher (`resumo_do_voucher` do backend). */
export type ResumoDoVoucher = {
  credito_nominal: string | null
  valor_negociado: string | null
  desconto: string | null
  acumulo: AcumuloDoVoucher | null
  politica_de_liberacao: LiberacaoDoVoucher | null
  tolerancia_dias: number | null
  aproveita_pagamento_atrasado: boolean
  competencia_atual: string
  proxima_virada: string
  saldo_disponivel: string
  saldo_congelado: string
  concedido_total: string
  consumido_total: string
  consumido_na_competencia: string
  inadimplente: boolean
  cobrancas: CobrancaDoVoucher[]
  /** só na visão da revenda */
  lancamentos?: LancamentoDoVoucher[]
}

export type SpecialContractListResponse = {
  contracts: SpecialContract[]
  total: number
}

export type CreateSpecialContractRequest = {
  name: string
  contract_type: TipoDeContratoEspecial
  customer_id: string
  start_date?: string
  end_date?: string | null
  currency?: string
  notes?: string | null // null limpa

  // Resource Block
  block_vcpu_limit?: number
  block_memory_gb_limit?: number
  block_storage_gb_limit?: number
  block_fixed_price?: number
  block_billing_cycle?: string
  overage_price_per_vcpu?: number
  overage_price_per_memory_gb?: number
  overage_price_per_storage_gb?: number
  block_scope?: string
  block_target_product_type?: string | null
  block_target_product_id?: string | null

  // Simple Discount
  discount_percentage?: number
  discount_scope?: string
  discount_target_product_type?: string | null
  discount_target_product_id?: string | null

  // Voucher (#1107)
  voucher_credit_amount?: number
  voucher_price?: number
  voucher_rollover?: AcumuloDoVoucher
  voucher_release_policy?: LiberacaoDoVoucher
  voucher_grace_days?: number | null
  voucher_late_payment_carry?: boolean
}

export type UpdateSpecialContractRequest = {
  name?: string
  end_date?: string | null
  notes?: string | null // null limpa
  block_vcpu_limit?: number
  block_memory_gb_limit?: number
  block_storage_gb_limit?: number
  block_fixed_price?: number
  overage_price_per_vcpu?: number | null // null limpa (excedente é opcional)
  overage_price_per_memory_gb?: number | null // null limpa (excedente é opcional)
  overage_price_per_storage_gb?: number | null // null limpa (excedente é opcional)
  block_scope?: string
  block_target_product_type?: string | null
  block_target_product_id?: string | null
  discount_percentage?: number
  discount_scope?: string
  discount_target_product_type?: string | null
  discount_target_product_id?: string | null
  // Voucher (#1107): vale a partir da próxima competência (00:00 de Brasília)
  voucher_credit_amount?: number
  voucher_price?: number
  voucher_rollover?: AcumuloDoVoucher
  voucher_release_policy?: LiberacaoDoVoucher
  voucher_grace_days?: number | null // null limpa (só existe na liberação na abertura)
  voucher_late_payment_carry?: boolean
}

export type ResourceBlockUsageSnapshot = {
  id: string
  special_contract_id: string
  snapshot_date: string
  billing_period: string
  total_vcpu: number
  total_memory_gb: number
  total_storage_gb: number
  overage_vcpu: number
  overage_memory_gb: number
  overage_storage_gb: number
  daily_overage_cost: number
  is_billed: boolean
  created_at: string
}

export type UsageHistoryResponse = {
  snapshots: ResourceBlockUsageSnapshot[]
  total: number
}

export type SpecialContractListParams = {
  customer_id?: string
  contract_type?: string
  status?: string
  skip?: number
  limit?: number
}

// ── Helpers ──────────────────────────────────────────────────────────────

export const getContractTypeLabel = (type: string): string => {
  switch (type) {
    case 'resource_block':
      return 'Bloco de Recursos'
    case 'simple_discount':
      return 'Desconto Simples'
    case 'voucher':
      return 'Voucher de desconto'
    default:
      return type
  }
}

export const getContractTypeColor = (type: string): string => {
  switch (type) {
    case 'resource_block':
      return 'indigo'
    case 'simple_discount':
      return 'emerald'
    case 'voucher':
      return 'violet'
    default:
      return 'gray'
  }
}

export const getContractStatusLabel = (status: string): string => {
  switch (status) {
    case 'active':
      return 'Ativo'
    case 'expired':
      return 'Expirado'
    case 'cancelled':
      return 'Cancelado'
    default:
      return status
  }
}

export const getContractStatusColor = (status: string): string => {
  switch (status) {
    case 'active':
      return 'emerald'
    case 'expired':
      return 'amber'
    case 'cancelled':
      return 'red'
    default:
      return 'gray'
  }
}

/**
 * Rótulo legível do escopo do contrato.
 *
 * Delega para `rotuloDoEscopo` da util. Mantém compatibilidade de assinatura
 * (sem os outros args) para chamadas legadas.
 *
 * @param scope O escopo.
 * @param contractType Modalidade (opcional; se omitido, assume desconto).
 * @param target Alvo com tipo e/ou nome do produto (opcional).
 */
export const getScopeLabel = (
  scope: string | null,
  contractType?: ModalidadeDoContrato,
  target?: { tipo?: string | null; produtoNome?: string | null },
): string => {
  if (!contractType) {
    // Compatibilidade: sem modalidade, assume desconto e rótulo simples
    return rotuloDoEscopo('simple_discount', scope as EscopoDoContrato, target)
  }
  return rotuloDoEscopo(contractType, scope as EscopoDoContrato, target)
}

// ── API — Reseller ──────────────────────────────────────────────────────

export const specialContractsApi = {
  /** Lista contratos especiais do reseller */
  list: (params?: SpecialContractListParams): Promise<SpecialContractListResponse> => {
    const query = new URLSearchParams()
    if (params?.customer_id) query.append('customer_id', params.customer_id)
    if (params?.contract_type) query.append('contract_type', params.contract_type)
    if (params?.status) query.append('status', params.status)
    if (params?.skip !== undefined) query.append('skip', params.skip.toString())
    if (params?.limit !== undefined) query.append('limit', params.limit.toString())
    const qs = query.toString()
    return apiClient.get(`/api/v1/reseller/special-contracts${qs ? `?${qs}` : ''}`)
  },

  /** Detalhe do contrato */
  get: (id: string): Promise<SpecialContract> => {
    return apiClient.get(`/api/v1/reseller/special-contracts/${id}`)
  },

  /** Criar contrato especial */
  create: (data: CreateSpecialContractRequest): Promise<SpecialContract> => {
    return apiClient.post('/api/v1/reseller/special-contracts', data)
  },

  /** Atualizar contrato */
  update: (id: string, data: UpdateSpecialContractRequest): Promise<SpecialContract> => {
    return apiClient.put(`/api/v1/reseller/special-contracts/${id}`, data)
  },

  /** Cancelar contrato */
  cancel: (id: string): Promise<{ message: string; id: string }> => {
    return apiClient.post(`/api/v1/reseller/special-contracts/${id}/cancel`, {})
  },

  /** Historico de uso (bloco) */
  getUsageHistory: (
    id: string,
    params?: { billing_period?: string; skip?: number; limit?: number },
  ): Promise<UsageHistoryResponse> => {
    const query = new URLSearchParams()
    if (params?.billing_period) query.append('billing_period', params.billing_period)
    if (params?.skip !== undefined) query.append('skip', params.skip.toString())
    if (params?.limit !== undefined) query.append('limit', params.limit.toString())
    const qs = query.toString()
    return apiClient.get(`/api/v1/reseller/special-contracts/${id}/usage${qs ? `?${qs}` : ''}`)
  },
}

export default specialContractsApi
